import React from 'react';
import { Row, Table } from 'antd';
import { I18n } from '@aws-amplify/core';

import { Actions } from './Actions';
import { ListBadges } from './components/ListBadges';
import { ModalDelete } from './components/ModalDelete';
import { useDeleteModal } from './hooks/useDeleteModal';
import { PaginationCustom } from '@/components/ui/Pagination';
import { useResolution886ReportFilter } from '@/context/Resolution886ReportFilter';
import type { Worker, Resolution886ItemList } from '@/types/Resolution886';
import type { ColumnsType } from 'antd/es/table';
import * as S from './styles';

interface TableListProps {
	loading?: boolean;
	data?: Resolution886ItemList[];
	total?: number;
	limit?: number;
	offset?: number;
}

export function TableList({
	loading = false,
	data = [],
	total = 0,
	limit = 5,
	offset = 0
}: Readonly<TableListProps>): JSX.Element {
	const { handleSetOffset, handleSetLimit } = useResolution886ReportFilter();
	const {
		selectedReport,
		isOpenDeleteModal,
		isLoadingDeleteModal,
		handleOpenDeleteModal,
		handleCloseDeleteModal,
		handleOnConfirmDeleteModal
	} = useDeleteModal();

	const columns: ColumnsType<Resolution886ItemList> = [
		{
			title: <S.CustomText>{I18n.get('Name')}</S.CustomText>,
			dataIndex: 'name',
			key: 'name',
			ellipsis: true,
			align: 'center',
			render: (name: string) => <S.CustomTableText>{name}</S.CustomTableText>
			// sorter: (a, b) => sortByName(a.name, b.name)
		},
		{
			title: <S.CustomText>{I18n.get('Workstation')}</S.CustomText>,
			dataIndex: 'workstation_name',
			key: 'workstation_name',
			ellipsis: true,
			align: 'center',
			render: (value: string) => <S.CustomTableText>{value ?? '-'}</S.CustomTableText>,
			responsive: ['lg']
			// sorter: (a, b) => sortByName(a.workstation, b.workstation)
		},
		{
			title: <S.CustomText>{I18n.get('Workers')}</S.CustomText>,
			dataIndex: 'workers',
			key: 'workers',
			ellipsis: true,
			align: 'center',
			render: (workers: Worker[]) => <ListBadges badges={workers} />,
			responsive: ['lg']
			// sorter: (a, b) => sortByName(a.workers, b.workers)
		},
		{
			title: <S.CustomText>{I18n.get('Creation date')}</S.CustomText>,
			dataIndex: 'creation_date',
			key: 'creation_date',
			ellipsis: true,
			align: 'center',
			render: (value: string) => <S.CustomTableText>{value}</S.CustomTableText>,
			responsive: ['lg']
			// sorter: (a, b) => sortByDate(a.creation_date, b.creation_date)
		},
		{
			title: <S.CustomText>{I18n.get('Review date')}</S.CustomText>,
			dataIndex: 'review_date',
			key: 'review_date',
			ellipsis: true,
			align: 'center',
			render: (value: string) => <S.CustomTableText>{value}</S.CustomTableText>,
			responsive: ['lg']
			// sorter: (a, b) => sortByDate(a.review_date, b.review_date)
		},
		{
			title: <S.CustomText>{I18n.get('Actions')}</S.CustomText>,
			key: 'actions',
			align: 'center',
			render: (_, value) => <Actions rowData={value} onDelete={() => handleOpenDeleteModal(value)} />
		}
	];

	return (
		<Row justify="center">
			<S.CustomTable span={24}>
				<Table
					rowKey="key"
					loading={loading}
					dataSource={data}
					columns={columns}
					pagination={false}
					showSorterTooltip={false}
				/>
				<PaginationCustom
					page={offset}
					total={total}
					maxPage={limit}
					loading={loading}
					setPage={handleSetOffset}
					setMaxPage={handleSetLimit}
				/>
			</S.CustomTable>
			<ModalDelete
				isFromReview={false}
				open={isOpenDeleteModal}
				loading={isLoadingDeleteModal}
				reportName={selectedReport?.name}
				onConfirm={handleOnConfirmDeleteModal}
				onCancel={handleCloseDeleteModal}
			/>
		</Row>
	);
}
